import {
  findLegIndexForStop,
  isListingStop,
  isLocateableStop,
  Itinerary,
  LocateableStop,
} from "~/api/resources/Itineraries";
import { listingPreviewFromStop } from "~/lib/listingPreview";
import { Fragment, useMemo, useRef, useState } from "react";
import { Box, useBreakpointValue } from "@chakra-ui/react";
import { useMapContext } from "~/providers/MapContextProvider";
import { BaseMap } from "../BaseMap";
import { getBoundsFromLonLats } from "../helpers";
import { StopMarker } from "../Markers";
import { StopPolylines } from "../Polylines";
import { ListingInfoWindow } from "../InfoWindows";
import { OptionalModal } from "../OptionalModal";
import { MapSwitcher } from "../MapSwitcher";
import { useSliderContext } from "../../Slider";
import { useLegContext } from "./LegProvider";
import { useIsMobile } from "~/lib/hooks";

export const ItineraryMap = ({ legs = [] }: Pick<Itinerary, "legs">) => {
  const legsWithStops = legs.filter((l) => l.stops.length > 0);
  const { mapSelected, setMapSelected } = useMapContext();
  const { activeIndex, setActiveIndex } = useSliderContext();
  const { isEast, legIndex, setLegIndex } = useLegContext();
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const toggleMaps = useBreakpointValue({ base: false, lg: false });
  const directedLegs = isEast ? legsWithStops : [...legsWithStops].reverse();
  const isMobile = useIsMobile();
  const stops = useMemo(() => {
    return (
      legIndex !== null && !isMobile
        ? directedLegs[legIndex].stops
        : directedLegs.flatMap((l) => l?.stops)
    ).filter(isLocateableStop);
  }, [directedLegs, legIndex, isMobile]);

  const mapRef = useRef<google.maps.Map>(null);

  const height = useBreakpointValue({
    base: "844px",
    md: "1194px",
    lg: "759px",
  });
  const getLegIndex = (stop: LocateableStop) =>
    findLegIndexForStop(directedLegs, stop.id);
  const activeStop = stops[activeIndex];
  return (
    <OptionalModal
      hasModal={!!toggleMaps}
      isOpen={mapSelected}
      onClose={() => setMapSelected(false)}
      size="full"
      motionPreset="none"
    >
      <Box pos="relative">
        {isMobile && showInfoWindow && (
          <ListingInfoWindow
            onClose={() => setShowInfoWindow(false)}
            listing={
              legIndex !== null && activeStop && isListingStop(activeStop)
                ? listingPreviewFromStop(activeStop)
                : undefined
            }
          />
        )}
        <MapSwitcher title="Communities" pos="absolute" w="100%" />
        <BaseMap
          zoom={16}
          ref={mapRef}
          onLoad={(map) => {
            const bounds = getBoundsFromLonLats(stops);
            map.fitBounds(bounds);
          }}
          mapContainerStyle={{
            height: height,
            borderRadius: "20px",
            overflow: "hidden",
          }}
        >
          {stops.map((stop, i) => {
            return (
              <Fragment key={stop.id}>
                <StopMarker
                  record={stop}
                  onClick={() => {
                    setLegIndex(getLegIndex(stop));
                    setActiveIndex(i);
                    setShowInfoWindow(true);
                  }}
                  focused={i === activeIndex && legIndex !== null}
                />

                <StopPolylines {...stop} />
              </Fragment>
            );
          })}
        </BaseMap>
      </Box>
    </OptionalModal>
  );
};
